import { Box, Paper, Typography, TextField, InputAdornment, IconButton } from '@mui/material';
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-hot-toast';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Btn from '../global/Btn';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/useAuthContext';
import i18next from 'i18next';

const FormLogin = () => {
    let lng = i18next.language;
    const navigate = useNavigate();
    const { login } = useAuthContext();
    
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const LoginSchema = Yup.object().shape({
        email: Yup.string()
            .required(lng === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required')
            .email(lng === 'ar' ? 'يجب إدخال بريد إلكتروني صحيح' : 'Must be a valid email'),
        password: Yup.string()
            .required(lng === 'ar' ? 'كلمة المرور مطلوبة' : 'Password is required'),
    });

    const { control, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(LoginSchema),
        defaultValues: {
            email: '',
            password: '',
        }
    });

    const SubmitForm = async (data) => {
        setLoading(true);
        try {
            const result = await login(data.email, data.password);
            if (result.status === 'success') {
                toast.success(result.message);
                navigate('/dashboard');
            } else {
                toast.error(result.message);
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || (lng === 'ar' ? 'حدث خطأ في الشبكة' : 'Network error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper
            elevation={24}
            sx={{
                px: { md: 4, xs: 3 },
                py: { md: 5, xs: 4 },
                borderRadius: 4,
                maxWidth: 420,
                mx: 'auto',
                bgcolor: 'background.paper',
                position: 'relative',
                zIndex: 1,
                backdropFilter: 'blur(10px)',
            }}
        >
            <Typography variant="h4" fontWeight={700} textAlign="center" sx={{ mb: 4, color: 'text.primary' }}>
                {lng === 'ar' ? 'تسجيل الدخول' : 'Log In'}
            </Typography>

            <Box component="form" onSubmit={handleSubmit(SubmitForm)} noValidate>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={lng === 'ar' ? 'البريد الإلكتروني' : 'Email Address'}
                                placeholder="example@example.com"
                                type="email"
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                            />
                        )}
                    />
                    
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label={lng === 'ar' ? 'كلمة المرور' : 'Password'}
                                placeholder="*********"
                                type={showPassword ? 'text' : 'password'}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Box>

                <Box mt={4} textAlign="center" sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Btn type="submit" width="100%" widthMob="100%" disabled={loading} sx={{ py: 1.5, fontSize: '1.1rem' }}>
                        {loading ? (lng === 'ar' ? 'جاري التحميل...' : 'Loading...') : (lng === 'ar' ? 'دخول' : 'Log In')}
                    </Btn>
                </Box>
            </Box>
        </Paper>
    );
}

export default FormLogin;